import { EventDetailsForView, EventWithCustomField } from "@ac-expo/api-types";
import { Button } from "@ac-expo/ui-components";
import {
  findOpeningStatus,
  formatDateRange,
  formatDateStringAsRanges,
  formatOpeningDateLines,
  formatOpeningRhythmTimetables,
  fromDatabaseFormat,
  groupSchedulesByTimetables,
  OpeningRhythm,
  OpeningStatus,
  toDatabaseFormat,
} from "@ac-expo/utils";
import { cn } from "@ui/utils";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useEffect, useMemo, useState } from "react";

const VISIBLE_DATE_LINES = 8;

const SPAN_FORMAT = { monthFormat: "MMMM", separator: " au " };

type EventScheduleSectionProps = {
  event: EventWithCustomField<EventDetailsForView>;
};

// Two rhythms can start on the same date with different hours, so the hours are part of the key.
const getRhythmKey = (rhythm: OpeningRhythm) =>
  `${rhythm.dates[0]}-${formatOpeningRhythmTimetables(rhythm)
    .map(({ hours }) => hours)
    .join("-")}`;

const formatOpeningDay = (date: string) =>
  format(fromDatabaseFormat(date), "EEEE d MMMM", { locale: fr });

const OpeningStatusCallout = ({ status }: { status: OpeningStatus }) => (
  <p className="mt-3 flex flex-wrap items-baseline gap-x-2 rounded-xl bg-primary-50 px-4 py-3 text-primary-700">
    <span className="text-sm font-semibold">
      {status.kind === "openToday"
        ? "Ouvert aujourd'hui"
        : "Prochaine ouverture"}
    </span>
    <span className="text-sm font-extrabold tabular-nums">
      {status.kind === "openToday"
        ? status.hours
        : `${formatOpeningDay(status.date)}, ${status.hours}`}
    </span>
  </p>
);

const OpeningRhythmBlock = ({
  rhythm,
  today,
}: {
  rhythm: OpeningRhythm;
  today: string | null;
}) => {
  const [areAllDatesVisible, setAreAllDatesVisible] = useState(false);

  const timetableLines = formatOpeningRhythmTimetables(rhythm);

  // A rhythm running over weeks is said by its span; its week days are already on the hours side.
  const dateLines = rhythm.runsOverWeeks
    ? [
        {
          label: `Du ${formatDateRange(rhythm.span.from, rhythm.span.to, SPAN_FORMAT)}`,
          lastDate: rhythm.span.to,
        },
      ]
    : formatOpeningDateLines(rhythm.ranges);

  const isFoldable = dateLines.length > VISIBLE_DATE_LINES;

  const visibleDateLines =
    areAllDatesVisible || !isFoldable
      ? dateLines
      : dateLines.slice(0, VISIBLE_DATE_LINES);

  const lastVisibleDate =
    visibleDateLines[visibleDateLines.length - 1]?.lastDate;

  const hiddenDateCount = rhythm.dates.filter(
    (date) => date > lastVisibleDate
  ).length;

  const handleToggleDates = () => setAreAllDatesVisible(!areAllDatesVisible);

  return (
    <div className="flex flex-col gap-2 py-5 sm:flex-row sm:gap-10">
      <div className="flex shrink-0 flex-col gap-1 sm:w-56">
        {timetableLines.map((line) => (
          <div key={line.hours} className="flex flex-col">
            {line.days && (
              <span className="text-xs font-semibold text-gray-500">
                {line.days}
              </span>
            )}
            <span className="text-base font-extrabold tabular-nums text-gray-900">
              {line.hours}
            </span>
          </div>
        ))}
        <span className="text-xs font-normal text-gray-500">
          {rhythm.dates.length > 1 ? `${rhythm.dates.length} dates` : "1 date"}
        </span>
      </div>

      <div className="flex min-w-0 flex-col items-start gap-1">
        {visibleDateLines.map((line) => (
          <p
            key={line.label}
            className={cn("text-sm font-semibold text-gray-700", {
              "text-gray-400": today !== null && line.lastDate < today,
            })}
          >
            {line.label}
          </p>
        ))}
        {isFoldable && (
          <Button
            variant="null"
            className="h-auto p-0 text-sm font-semibold text-primary-600 underline underline-offset-2"
            aria-expanded={areAllDatesVisible}
            onClick={handleToggleDates}
          >
            {areAllDatesVisible
              ? "Voir moins"
              : `Voir les ${hiddenDateCount} autres dates`}
          </Button>
        )}
      </div>
    </div>
  );
};

export const EventScheduleSection = ({ event }: EventScheduleSectionProps) => {
  const schedules = event.dates?.schedules;
  const exceptionalClosingDates = event.dates?.exceptionalClosingDates;

  const rhythms = useMemo(
    () =>
      groupSchedulesByTimetables(
        schedules ?? [],
        exceptionalClosingDates ?? []
      ),
    [schedules, exceptionalClosingDates]
  );

  // What counts as past depends on the visitor's own day, so it is only known after hydration.
  const [today, setToday] = useState<string | null>(null);
  const [status, setStatus] = useState<OpeningStatus | null>(null);

  useEffect(() => {
    const now = new Date();

    setToday(toDatabaseFormat(now));
    setStatus(findOpeningStatus(rhythms, now));
  }, [rhythms]);

  if (rhythms.length === 0) return null;

  const closingDates = (exceptionalClosingDates ?? []).map(({ date }) => date);

  return (
    <section className="py-6">
      <h4 className="text-lg font-extrabold text-gray-900">
        Dates et horaires
      </h4>

      {status && <OpeningStatusCallout status={status} />}

      <div className="mt-2 divide-y divide-gray-200">
        {rhythms.map((rhythm) => (
          <OpeningRhythmBlock
            key={getRhythmKey(rhythm)}
            rhythm={rhythm}
            today={today}
          />
        ))}
      </div>

      {closingDates.length > 0 && (
        <p className="pt-4 text-sm font-normal text-gray-500">
          {closingDates.length > 1
            ? "Fermé exceptionnellement les "
            : "Fermé exceptionnellement le "}
          {formatDateStringAsRanges(closingDates)}
        </p>
      )}
    </section>
  );
};
